import { CloudMast } from "./CloudMast";
import type { DeepLinkUiState } from "../lib/deep-link";

// Landing for the "your analysis is ready" email's deep link
// (pilot.plaintosee.cc/#run=<id>&token=<token>). It renders ABOVE the
// access-code gate (the run-token in the link is the auth), so someone who
// closed the tab — or opens the email on another device — can get back to
// their finished result. Reuses the shared landing chrome; no new CSS.
//
// The result is delivered as the same encrypted download the in-app flow
// produces — ciphertext only. The passphrase was shown ONCE at submit time
// and Aurora discarded its copy when the file was locked (audit B7), so
// this link in the wrong hands yields nothing readable. This screen is just
// the gate-free doorway.

export function DeepLinkLanding({
  state,
  onDownload,
  downloading,
  error,
  onRetry,
  mobile,
}: {
  state: DeepLinkUiState;
  onDownload: () => void;
  downloading?: boolean;
  /** A failed download attempt (e.g. the run was purged between check + click) —
   *  shown under the CTA so the user isn't left wondering. */
  error?: string;
  /** Re-check the run (used by the "running" state's "Check again" button). */
  onRetry?: () => void;
  /** Opened on a phone/tablet (lib/deep-link isMobileUa). The downloaded file
   *  needs a computer to open (phones preview it without running it), so the
   *  ready state adds a come-back-on-a-desktop hint. Download stays available —
   *  it consumes nothing and the link keeps working for the full window. */
  mobile?: boolean;
}) {
  return (
    <main className="landing">
      <div className="landing-wrap">
        <CloudMast />

       <section className="deeplink-body">
        {state === "loading" && (
          <>
            <h1 className="landing-title">Finding your analysis…</h1>
            <p className="landing-lede">One moment.</p>
          </>
        )}

        {state === "ready" && mobile && (
          /* On a phone the file is dead weight — it only opens on a computer.
             A prominent download CTA here INVITED a useless phone download
             (Rich, live 2026-06-11), so the mobile state leads with the real
             action (come back from a computer; the link keeps working) and
             demotes download to a quiet escape hatch. Downloading consumes
             nothing (one_time is never sent from this path), so the hatch is
             safe for the move-it-myself crowd. */
          <>
            <h1 className="landing-title">Your analysis is ready.</h1>
            <p className="landing-lede">
              It’s waiting for you — but it only opens on a computer, not a
              phone. This link keeps working for 7 days: when you’re at your
              computer, open this same email there and tap the
              link&nbsp;again.
            </p>
            <p className="landing-mobile-hint">
              Need the file on this phone anyway?{" "}
              <button
                type="button"
                className="landing-link landing-link-btn"
                onClick={onDownload}
                disabled={downloading}
              >
                {downloading ? "Preparing your file…" : "Download it here"}
              </button>{" "}
              — you can move it to your computer later. It opens with the
              passphrase you saved when you started the&nbsp;analysis.
            </p>
            {error && (
              <p className="landing-lede" role="alert" style={{ color: "#7a1f1f" }}>
                {error}
              </p>
            )}
          </>
        )}

        {state === "ready" && !mobile && (
          <>
            <h1 className="landing-title">Your analysis is ready.</h1>
            <p className="landing-lede">
              Download your finished analysis below. It opens in any browser,
              offline, with the passphrase you saved when you started the
              analysis. We don’t keep a copy of your passphrase, so the file
              stays locked without it — and we can’t recover it for&nbsp;you.
            </p>
            <button
              type="button"
              className="landing-cta"
              onClick={onDownload}
              disabled={downloading}
            >
              {downloading ? "Preparing your file…" : "Download your analysis →"}
            </button>
            {error && (
              <p className="landing-lede" role="alert" style={{ color: "#7a1f1f" }}>
                {error}
              </p>
            )}
          </>
        )}

        {state === "running" && (
          <>
            <h1 className="landing-title">Almost there.</h1>
            <p className="landing-lede">
              Your analysis is still finishing. Check back in a few minutes —
              this link will open it once it’s done.
            </p>
            {onRetry && (
              <button type="button" className="landing-cta" onClick={onRetry}>
                Check again →
              </button>
            )}
          </>
        )}

        {state === "expired" && (
          <>
            <h1 className="landing-title">This link is no longer available.</h1>
            <p className="landing-lede">
              Finished analyses are kept for up to 7 days, then deleted. You’ll
              need to run the analysis again.
            </p>
          </>
        )}

        {state === "unreachable" && (
          /* The status CHECK failed, not the link — network drop or a server
             hiccup. Never phrase this as expiry: telling someone on flaky
             wifi that their analysis is gone sends them off to re-run a
             finished, paid run (sweep, 2026-07-10). */
          <>
            <h1 className="landing-title">We couldn’t check on your analysis.</h1>
            <p className="landing-lede">
              This device couldn’t reach Aurora just now — that’s usually the
              connection, not your analysis. Your link is still good. Try
              again in a moment.
            </p>
            {onRetry && (
              <button type="button" className="landing-cta" onClick={onRetry}>
                Try again →
              </button>
            )}
          </>
        )}
       </section>

        <footer className="landing-foot">
          <div className="landing-foot-left">
            Aurora · A product of Plain to See LLC.
          </div>
          <div className="landing-foot-right">&copy; 2026 Aurora</div>
        </footer>
      </div>
    </main>
  );
}
